/**
 * Dutch cities mapping to provinces
 * Handles ambiguous city names (cities that exist in multiple provinces)
 */

export interface CityInfo {
    name: string;
    province: string;
    municipality?: string;
}

export const NL_CITIES: CityInfo[] = [
    { name: "Amsterdam", province: "Noord-Holland" },
    { name: "Rotterdam", province: "Zuid-Holland" },
    { name: "Den Haag", province: "Zuid-Holland" },
    { name: "Utrecht", province: "Utrecht" },
    { name: "Eindhoven", province: "Noord-Brabant" },
    { name: "Groningen", province: "Groningen" },
    { name: "Tilburg", province: "Noord-Brabant" },
    { name: "Almere", province: "Flevoland" },
    { name: "Breda", province: "Noord-Brabant" },
    { name: "Nijmegen", province: "Gelderland" },
    { name: "Enschede", province: "Overijssel" },
    { name: "Haarlem", province: "Noord-Holland" },
    { name: "Arnhem", province: "Gelderland" },
    { name: "Amersfoort", province: "Utrecht" },
    { name: "Zaanstad", province: "Noord-Holland" },
    { name: "Apeldoorn", province: "Gelderland" },
    { name: "'s-Hertogenbosch", province: "Noord-Brabant" },
    { name: "Hoofddorp", province: "Noord-Holland" },
    { name: "Maastricht", province: "Limburg" },
    { name: "Leiden", province: "Zuid-Holland" },
    { name: "Dordrecht", province: "Zuid-Holland" },
    { name: "Zoetermeer", province: "Zuid-Holland" },
    { name: "Zwolle", province: "Overijssel" },
    { name: "Deventer", province: "Overijssel" },
    { name: "Born", province: "Limburg" },
    { name: "Sittard", province: "Limburg" },
    { name: "Geleen", province: "Limburg" },
    { name: "Heerlen", province: "Limburg" },
    { name: "Venlo", province: "Limburg" },
    { name: "Leeuwarden", province: "Fryslân" },
    { name: "Hilversum", province: "Noord-Holland" },
    { name: "Amstelveen", province: "Noord-Holland" },
    { name: "Oss", province: "Noord-Brabant" },
    { name: "Súdwest-Fryslân", province: "Fryslân" },
    { name: "Hengelo", province: "Overijssel" },
    { name: "Roosendaal", province: "Noord-Brabant" },
    { name: "Schiedam", province: "Zuid-Holland" },
    { name: "Lelystad", province: "Flevoland" },
    { name: "Leidschendam-Voorburg", province: "Zuid-Holland" },
    { name: "Alphen aan den Rijn", province: "Zuid-Holland" },
    { name: "Spijkenisse", province: "Zuid-Holland" },
    { name: "Almelo", province: "Overijssel" },
    { name: "Vlaardingen", province: "Zuid-Holland" },
    { name: "Gouda", province: "Zuid-Holland" },
    { name: "Zaandam", province: "Noord-Holland" },
    { name: "Hoorn", province: "Noord-Holland" },
    { name: "Velsen", province: "Noord-Holland" },
    { name: "Assen", province: "Drenthe" },
    { name: "Capelle aan den IJssel", province: "Zuid-Holland" },
    { name: "Veenendaal", province: "Utrecht" },
    { name: "Nieuwegein", province: "Utrecht" },
    { name: "Katwijk", province: "Zuid-Holland" },
    { name: "Zeist", province: "Utrecht" },
    { name: "Hardenberg", province: "Overijssel" },
    { name: "Den Helder", province: "Noord-Holland" },
    { name: "Doetinchem", province: "Gelderland" },
    { name: "Terneuzen", province: "Zeeland" },
    { name: "Hoogeveen", province: "Drenthe" },
    { name: "Heerhugowaard", province: "Noord-Holland" },
    { name: "Oosterhout", province: "Noord-Brabant" },

    // --- AMBIGUOUS CITIES (Exist in multiple provinces) ---
    { name: "Laren", province: "Noord-Holland", municipality: "Laren (NH)" },
    { name: "Laren", province: "Gelderland", municipality: "Lochem" },

    { name: "Beek", province: "Limburg", municipality: "Beek" },
    { name: "Beek", province: "Gelderland", municipality: "Berg en Dal" },

    { name: "Stein", province: "Limburg", municipality: "Stein" },
    { name: "Stein", province: "Zuid-Holland", municipality: "Krimpenerwaard" },

    { name: "Eemnes", province: "Utrecht" },
    { name: "Baarn", province: "Utrecht" },

    { name: "Midsland", province: "Fryslân" },
    { name: "Nes", province: "Fryslân", municipality: "Ameland" },
    { name: "Nes", province: "Fryslân", municipality: "Heerenveen" },
    { name: "Nes", province: "Fryslân", municipality: "Noardeast-Fryslân" },

    { name: "Bergen", province: "Noord-Holland" },
    { name: "Bergen", province: "Limburg" },

    { name: "Hengelo", province: "Overijssel", municipality: "Hengelo" },
    { name: "Hengelo", province: "Gelderland", municipality: "Bronckhorst" },

    { name: "Epe", province: "Gelderland" },
    { name: "Epe", province: "Noord-Rhine-Westphalia" }, // (Technically Duitsland, but often confused)

    { name: "Monster", province: "Zuid-Holland" },

    { name: "Middelburg", province: "Zeeland" },
    { name: "Middelburg", province: "Zuid-Holland", municipality: "Bodegraven-Reeuwijk" },

    { name: "Sluis", province: "Zeeland" },
    { name: "Sluis", province: "Utrecht" },

    { name: "De Bilt", province: "Utrecht" },
    { name: "Bilthoven", province: "Utrecht" },
];

/**
 * Find province for a city name
 * Returns all matching provinces to handle ambiguity
 */
export function findProvincesForCity(cityName: string): CityInfo[] {
    if (!cityName) return [];
    const normalized = cityName.trim().toLowerCase();
    return NL_CITIES.filter(c => c.name.toLowerCase() === normalized);
}

/**
 * Get unique city names for search
 */
export function getAvailableCities(): string[] {
    const cities = NL_CITIES.map(c => c.name);
    return [...new Set(cities)].sort();
}
